import { normalize } from "normalizr";
import { Dispatch, Middleware, MiddlewareAPI } from "redux";
import { SavingNotifier } from "~/sharedComponents/feedback/";
import { XhrDeferredType } from "../../EasyProjectsCommon/General";
import { OAuthClient } from "../../EasyProjectsCommon/OAuthClient";
import { UnsavedChangesNotifier } from "../../EasyProjectsCommon/UnsavedChangesNotifier";
import { ApiError, ApiErrorTypes, ApiRequestAction } from "../Actions";
import { ApiAction } from "../Actions/apiActions";
import { EntitiesActionTypes, ReconstructEntitiesAction, UpdateEntitiesAction } from "../Entities/actions";
import { apiCallTypes, apiMiddlewareActionTypes } from "./apiMiddlewareActionTypes";

export type ValidationError = server.validation.ValidationItemError & {
    isTouched?: boolean;
};

export interface RequestErrorAction<T extends string = any> {
    type: T;
    errors: ValidationError[];
    requestData?: any;
    additionalData?: any;
    responseInfo?: {
        statusCode: number;
        statusText: string;
    };
    formName?: string;
}

export interface SuccessRequestAction<T extends string = any, P = any> {
    type: T;
    payload: P;
    requestData?: any;
    additionalData?: any;
    formName?: string;
}
export interface ApiException<T extends string = any, B extends ApiError = any, R = any> {
    type: T;
    body: B;
    requestData?: R;
}

export const doUpdateEntities = (entities: any, entityKey?: string): UpdateEntitiesAction => ({
    type: EntitiesActionTypes.UPDATE_ENTITIES,
    entities,
    entityKey,
});

export const doReconstructEntities = (entities: any, entityKey?: string): ReconstructEntitiesAction => ({
    type: EntitiesActionTypes.RECONSTRUCT_ENTITIES,
    entityKey,
    entities,
});

export const apiMiddleware: Middleware =
    <D extends Dispatch, S = any>(store: MiddlewareAPI<D, S>) =>
    next =>
    (action: any | ApiAction) => {
        if (action.type !== apiMiddlewareActionTypes.API_CALL) {
            return next(action);
        }
        const {
            endpoint,
            body,
            types = {},
            additionalParams = {},
            formName,
            schema,
            reconstructEntities,
            additionalData,
        } = <ApiAction>action;
        const {
            requestType = apiMiddlewareActionTypes.API_START,
            successType = apiMiddlewareActionTypes.API_SUCCESS,
            failureType = apiMiddlewareActionTypes.API_FAILURE,
            userFriendlyExcType = apiMiddlewareActionTypes.API_USER_FRIENDLY_EXC,
            objectNotFoundType = apiMiddlewareActionTypes.API_OBJECT_NOT_FOUND,
        } = types;

        const requestAction: ApiRequestAction = { type: requestType, requestData: body, additionalData };
        store.dispatch(requestAction);

        const optParams: Partial<OptionalParams> = {
            ignoreErrorsList: [ApiErrorTypes.ObjectNotFound, ApiErrorTypes.UserFriendlyError],
            ignoreErrors: additionalParams.ignoreErrors,
            responseSchema: additionalParams.responseSchema,
        };
        const getXHR = () => {
            if (additionalParams.postWithAuthToken) {
                switch (additionalParams.apiCallType) {
                    case apiCallTypes.MULTIPART_FORM:
                        return OAuthClient.postMultipart(endpoint, body, optParams) as XhrDeferredType;
                    default:
                        return OAuthClient.postJson(endpoint, body, optParams) as XhrDeferredType;
                }
            }
            switch (additionalParams.apiCallType) {
                case apiCallTypes.MULTIPART_FORM:
                    return ep.common.postMultipart(endpoint, body, optParams);
                default:
                    return additionalParams.repost
                        ? ep.common.repostJson(endpoint, body, null, optParams)
                        : ep.common.postJson(endpoint, body, null, optParams);
            }
        };

        const xhr = getXHR();
        if (additionalParams && additionalParams.withActionFeedback) {
            SavingNotifier.registerPromise(xhr);
        }

        xhr.then(
            (promise: any) => {
                if (promise && promise.HasErrors) {
                    const action: RequestErrorAction = {
                        type: failureType,
                        errors: promise.Errors,
                        formName,
                        requestData: body,
                        additionalData,
                    };
                    store.dispatch(action);
                    return;
                }

                const resData = schema ? normalize(promise, schema) : promise;

                if (!!schema) {
                    if (reconstructEntities) {
                        store.dispatch(doReconstructEntities(resData.entities, reconstructEntities as string));
                    } else {
                        store.dispatch(doUpdateEntities(resData.entities));
                    }
                }

                const action: SuccessRequestAction = {
                    type: successType,
                    payload: resData,
                    requestData: body,
                    additionalData,
                    formName,
                };

                store.dispatch(action);
            },
            (promise: any) => {
                const resp = promise && promise.responseJSON;
                const skipCustomHandling = additionalParams && additionalParams.skipUserFriendlyDialog;
                if (
                    !skipCustomHandling &&
                    resp &&
                    resp.ErrorType &&
                    (resp.ErrorType === ApiErrorTypes.UserFriendlyError ||
                        resp.ErrorType === ApiErrorTypes.ObjectNotFound)
                ) {
                    const type =
                        resp.ErrorType === ApiErrorTypes.UserFriendlyError ? userFriendlyExcType : objectNotFoundType;
                    const action: ApiException = { type, body: resp, requestData: body };
                    store.dispatch(action);
                }

                store.dispatch({
                    type: failureType,
                    body: resp,
                    requestData: body,
                    responseInfo: promise && {
                        statusCode: promise.status,
                        statusText: promise.statusText,
                    },
                });
            }
        );
        if (additionalParams && additionalParams.enableUnsavedChangesNotifier) {
            UnsavedChangesNotifier.instance.trackPromise(xhr);
        }
        return xhr;
    };
